import { useMemo } from 'react';

import type { ThemeTabType } from '../../../../entities/post/models';

import {
  getBrandLevelColumns,
  getProductLevelColumns,
  getThemeLevelColumns,
} from '../configs/table.columns';

import type { METRIC, StatisticData, ThemeStatisticData } from '@/entities/insight';
import { PageNavigation } from '@/shared/components/page-navigation/page-navigation';
import { StaticTable } from '@/shared/components/static-table/static-table';
import { useStaticTable } from '@/shared/components/static-table/use-static-table';
import type { Table } from '@tanstack/react-table';

interface Props {
  currentLevel: 'theme' | 'brand' | 'product';
  selectedTab: ThemeTabType;
  handleRowClick: (statistic: StatisticData | ThemeStatisticData, event: React.MouseEvent) => void;
  handleDrillDown: (statistic: StatisticData | ThemeStatisticData) => void;
  metric: METRIC;
  themes: StatisticData[];
  isLoading: boolean;
}

const PAGE_SIZE = 10;

export function UnifiedThemeTable(props: Props) {
  const { currentLevel, selectedTab, metric, themes, isLoading } = props;

  // Filter data based on selected tab (only at theme level)
  const filteredData = useMemo(() => {
    if (currentLevel !== 'theme' || selectedTab === 'all') {
      return themes;
    }

    // At theme level, filter by sentiment
    const themeData = themes as ThemeStatisticData[];
    return themeData.filter((item) => {
      if (selectedTab === 'positive') return item.sentiment === 'positive';
      if (selectedTab === 'negative') return item.sentiment === 'negative';
      return true;
    });
  }, [themes, currentLevel, selectedTab]);

  // Get appropriate columns based on current level
  const columns = useMemo(() => {
    switch (currentLevel) {
      case 'theme':
        return getThemeLevelColumns(selectedTab, metric, props.handleDrillDown);
      case 'brand':
        return getBrandLevelColumns(metric, props.handleDrillDown);
      case 'product':
        return getProductLevelColumns(metric);
      default:
        return [];
    }
  }, [currentLevel, selectedTab, metric, props.handleDrillDown]);

  const { table, gridTemplateColumns } = useStaticTable({
    data: filteredData || [],
    columns: columns as never, // Type assertion needed due to level-specific column types
    pageSize: PAGE_SIZE,
    showIndexColumn: false, // We provide our own index column in the column configs
  });

  return (
    <>
      <div className="p-6 py-0">
        <StaticTable
          pageSize={PAGE_SIZE}
          table={table as Table<ThemeStatisticData | StatisticData>}
          gridTemplateColumns={gridTemplateColumns}
          onRowClick={props.handleRowClick}
          isLoading={isLoading}
        />
      </div>
      {!isLoading && <PageNavigation className="border-base-border border-t" table={table} />}
    </>
  );
}
